import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarClock, CalendarDays, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Amount visibility is limited to Admin & Managers per Iter33 policy.
// Every other role sees the count only (still tap-able → full list).
const AMOUNT_VISIBLE_ROLES = new Set([
  'Super Admin', 'Admin', 'Branch Manager',
  'Accountant',  // finance role — needs to see money numbers
]);

function fmt(n) { return `PKR ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`; }

// Three clickable summary tiles — Upcoming · Due Today · Overdue — that
// deep-link into the Installment Reports tab pre-filtered by status. Any
// role dashboard can drop this in with `<InstallmentSummaryCards />`.
export default function InstallmentSummaryCards() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeAmounts = AMOUNT_VISIBLE_ROLES.has(user?.role);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/reports/installments/summary`,
          { withCredentials: true, timeout: 8000 });
        if (!cancel) setData(data);
      } catch { if (!cancel) setData({}); }
    };
    load();
    // Refresh once a minute; the endpoint is cached 20 s server-side.
    const iv = setInterval(load, 60000);
    return () => { cancel = true; clearInterval(iv); };
  }, []);

  const open = (status) => navigate(`/reports?tab=installments&status=${status}`);

  const tiles = [
    { k: 'upcoming',  title: 'Upcoming',  Icon: CalendarClock, color: 'from-sky-600/25 to-sky-500/5 border-sky-500/40 hover:ring-sky-500/60',       iconColor: 'text-sky-400',    d: data?.upcoming },
    { k: 'due_today', title: 'Due Today', Icon: CalendarDays,  color: 'from-amber-600/25 to-amber-500/5 border-amber-500/40 hover:ring-amber-500/60', iconColor: 'text-amber-400',  d: data?.due_today },
    { k: 'overdue',   title: 'Overdue',   Icon: AlertTriangle, color: 'from-rose-600/25 to-rose-500/5 border-rose-500/40 hover:ring-rose-500/60',   iconColor: 'text-rose-400',   d: data?.overdue  },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="installment-summary-cards">
      {tiles.map((t) => (
        <button key={t.k} onClick={() => open(t.k)}
          data-testid={`inst-summary-card-${t.k}`}
          className={`text-left rounded-sm border p-4 bg-gradient-to-br transition-all
                       hover:-translate-y-0.5 hover:ring-2 ${t.color}`}>
          <div className="flex items-center justify-between">
            <span className="text-slate-300 text-sm uppercase tracking-wider">{t.title}</span>
            <t.Icon className={`h-5 w-5 ${t.iconColor}`} />
          </div>
          {canSeeAmounts ? (
            <>
              <div className="mt-3 text-2xl font-bold text-white">{fmt(t.d?.amount)}</div>
              <div className="text-xs text-slate-400 mt-1">{(t.d?.count ?? 0)} installments · click for full list</div>
            </>
          ) : (
            <>
              <div className="mt-3 text-3xl font-bold text-white">{(t.d?.count ?? 0).toLocaleString()}</div>
              <div className="text-xs text-slate-400 mt-1">installments · click for full list</div>
            </>
          )}
        </button>
      ))}
    </div>
  );
}
